import { Location } from 'history';
import Page from 'mk2/components/Page';
import TrackPageview from 'mk2/components/TrackPageview';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { getLogger } from 'mk2/logger';
import PageDefinition from 'mk2/pages/Page';
import { AppState, getRequestBaseUrl, getRequestDeviceMobile, getRequestPermissions } from 'mk2/reducers';
import { MenuSelection } from 'mk2/schemas';
import RouterContext from 'mk2/services/RouterContext';
import { Store } from 'mk2/store/configureStore';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { connect } from 'react-redux';
import defaultMapDispatchToPropsFactories from 'react-redux/es/connect/mapDispatchToProps';
import defaultMapStateToPropsFactories from 'react-redux/es/connect/mapStateToProps';
import defaultMergePropsFactories from 'react-redux/es/connect/mergeProps';
import selectorFactory from 'react-redux/es/connect/selectorFactory';
import { storeShape, subscriptionShape } from 'react-redux/es/utils/PropTypes';
import shallowEqual from 'react-redux/es/utils/shallowEqual';
import Subscription from 'react-redux/es/utils/Subscription';
import { match, matchPath, RouteComponentProps } from 'react-router';

const logger = getLogger('Application');

const dummyState = {};

function noop() {} // tslint:disable-line

function makeSelectorStateful(sourceSelector, store) {
    // wrap the selector in an object that tracks its results between runs.
    const selector: Selector = {
        run: function runComponentSelector(props) {
            try {
                const nextProps = sourceSelector(store.getState(), props);
                if (nextProps !== selector.props || selector.error) {
                    selector.shouldComponentUpdate = true;
                    selector.props = nextProps;
                    selector.error = null;
                }
            } catch (error) {
                selector.shouldComponentUpdate = true;
                selector.error = error;
            }
        },
    };

    return selector;
}
function matchFactory(arg, factories, name) {
    for (let i = factories.length - 1; i >= 0; i--) {
        const result = factories[i](arg);
        if (result) {
            return result;
        }
    }

    return (dispatch, options) => {
        throw new Error(`Invalid value of type ${typeof arg} for ${name} argument when connecting component ${options.wrappedComponentName}.`);
    };
}
function strictEqual(a, b) { return a === b; }

export type AppName =
    'about' | 'bazaar' | 'bo' | 'blogs' | 'dash' | 'forum' | 'hearts' | 'home' | 'mail' | 'market' | 'settings' | 'strollers' | 'unknown' | 'users' | 'wall' | 'wiki';

interface BaseRoute {
    app: AppName;
    exact?: boolean;
    strict?: boolean;
    path?: string;
}

export interface StaticRoute extends BaseRoute {
    page: PageDefinition<any, any, any>;
}

export interface DynamicRoute extends BaseRoute {
    page?: PageDefinition<any, any, any>;
    getPage: () => Promise<{ default: new() => PageDefinition<any, any, any>}>;
    modules: string[];
}

export type Route = StaticRoute | DynamicRoute;

interface ChildrenProps {
    app: AppName;
    content: (JSX.Element | null | false);
    rightContent: (JSX.Element | null | false);
    halfpage: (JSX.Element | null | false);
}

interface Selector {
    shouldComponentUpdate?: boolean;
    props?: any;
    error?: Error | null;

    run(props: PageProps);
}

interface OwnProps {
    className?: string;
    routes: Route[]; // Route order matters
    location?: Location;
    children: (props: ChildrenProps) => React.ReactNode;

    store?: Store<any>;
}

interface StateProps {
    baseUrl: string;
    mobile: boolean;
    requestUserPermissions: string[];
}

interface DispatchProps {

}

type Props = OwnProps & StateProps & DispatchProps;
type PageProps = RouteComponentProps<any> & {
    app: AppName;
    baseUrl: string;
    mobile: boolean;
    requestUserPermissions: string[];
    pageInstance: PageDefinition<any, any, any>;
    children: (props: ChildrenProps) => React.ReactNode
    menuSelection?: MenuSelection;
};

/**
 * Inspired by react-redux/src/components/connectAdvanced
 *
 * TODO: Fix hot reloading
 */
class Application extends React.Component<Props> {

    public static contextTypes = {
        router: PropTypes.shape({
            history: PropTypes.object.isRequired,
            route: PropTypes.object.isRequired,
            staticContext: PropTypes.object,
        }),
        store: storeShape,
        storeSubscription: subscriptionShape,
        loadable: PropTypes.shape({
            report: PropTypes.func.isRequired,
        }),
    };

    public static childContextTypes = {
        storeSubscription: subscriptionShape,
    };

    private propsMode: boolean;
    private subscription: Subscription;
    private notifyNestedSubs: () => void;
    private selector: Selector;
    private store: Store<any>;

    private currentRoute: Route;
    private currentProps: PageProps;

    private loading: boolean = false;

    public constructor(props: Props, context: any) {
        super(props, context);

        this.store = props.store || context.store;
        this.propsMode = Boolean(props.store);

        this.initSubscription();
        this.selectCurrentPage(props, context);
        // this.initSelector();
    }

    public getChildContext() {
        const subscription = this.propsMode ? null : this.subscription;
        return {
            storeSubscription: subscription || this.context.storeSubscription,
        };
    }

    public componentDidMount() {
        if (!this.loading) {
            this.subscription.trySubscribe();
            this.selector.run(this.currentProps);
            if (this.selector.shouldComponentUpdate) {
                this.forceUpdate();
            }
        }
    }

    public componentWillReceiveProps(nextProps: Props, nextContext: any) {
        if (this.loading) {
            return;
        }

        this.selectCurrentPage(nextProps, nextContext);
        this.selector.run(this.currentProps);
        // if (this.props.routes !== nextProps.routes) {
        //     this.connectRoutes(nextProps);
        // }
    }

    public shouldComponentUpdate() {
        return this.loading ? false : this.selector.shouldComponentUpdate;
    }

    public componentWillUnmount() {
        if (this.subscription) {
            this.subscription.tryUnsubscribe();
        }
        this.subscription = null;
        this.notifyNestedSubs = noop;
        this.store = null;
        this.selector.run = noop;
        this.selector.shouldComponentUpdate = false;
    }

    public render() {
        if (this.loading) {
            return null;
        }

        const selector = this.selector;
        selector.shouldComponentUpdate = false;

        if (selector.error) {
            throw selector.error;
        } else {
            return (
                <RouterContext.Provider value={{ routes: this.props.routes }}>
                    <TrackPageview app={selector.props.app}>
                        <Page {...this.addExtraProps(selector.props)} />
                    </TrackPageview>
                </RouterContext.Provider>
            );
        }
    }

    private computeMatch({ location, path, strict, exact }, router) {
        const { route } = router;
        const pathname = (location || route.location).pathname;

        return path ? matchPath(pathname, { path, strict, exact }) : route.match;
    }

    private selectCurrentPage(props: Props, context: any) {
        const { baseUrl, children, routes, mobile, requestUserPermissions } = props;
        const { history, route, staticContext } = context.router;
        const location = props.location || route.location;

        let matched: Route;
        let routeMatch: match<any>;

        for (const current of routes) {
            const { path, exact, strict } = current;
            routeMatch = this.computeMatch({ path, exact, strict, location }, context.router);
            if (routeMatch) {
                matched = current;
                break;
            }
        }

        if (!matched) {
            // Should not happen
            throw new Error('Not route matched - did you forget to add NotFoundPage as last?');
        }

        const resolved = () => {
            this.currentProps = {
                // react-router > Route
                match: routeMatch,
                location,
                history,
                staticContext,
                // pass app name
                app: matched.app,
                // pass our page instance
                pageInstance: matched.page,
                // pass baseUrl
                baseUrl,
                // pass mobile
                mobile,
                requestUserPermissions,
                // connect children
                children,
            };

            if (this.currentRoute !== matched) {
                // Cleanup
                if (this.selector) {
                    this.selector.run = noop;
                    this.selector.shouldComponentUpdate = false;
                }

                this.currentRoute = matched;

                const registering = matched.page.registerInStore(this.store);

                if (registering) {
                    this.loading = true;

                    registering.then(() => {
                        // Create new selector if reducer and sagas are loaded
                        this.initSelector();
                        this.loading = false;
                        // Reload
                        this.setState(dummyState);
                    }).catch((err) => {
                        logger.error(err);
                    });

                } else {
                    const wasLoading = this.loading;
                    this.loading = false;
                    this.initSelector();
                    if (wasLoading) {
                        this.setState(dummyState);
                    }
                }
            }
        };

        if ('modules' in matched) {
            if (this.context.loadable && Array.isArray(matched.modules)) {
                matched.modules.forEach((moduleName) => {
                    this.context.loadable.report(moduleName);
                });
            }
        }

        if (!matched.page) {
            this.loading = true;

            (matched as DynamicRoute).getPage().then(({ default: PageDef }) => {
                matched.page = new PageDef();
                resolved();
            }, (err) => {
                logger.error(err);
            });
        } else {
            resolved();
        }
    }

    private initSelector() {
        const initMapStateToProps = matchFactory(this.currentRoute.page.mapStateToProps(), defaultMapStateToPropsFactories, 'mapStateToProps');
        const initMapDispatchToProps = matchFactory(this.currentRoute.page.mapDispatchToProps(), defaultMapDispatchToPropsFactories, 'mapDispatchToProps');
        const initMergeProps = matchFactory(this.currentRoute.page.mergeProps(), defaultMergePropsFactories, 'mergeProps');

        const sourceSelector = selectorFactory(this.store.dispatch, {
            initMapStateToProps,
            initMapDispatchToProps,
            initMergeProps,
            pure: true,
            areStatesEqual: strictEqual,
            areOwnPropsEqual: shallowEqual,
            areStatePropsEqual: shallowEqual,
            areMergedPropsEqual: shallowEqual,
            shouldHandleStateChanges: Boolean(this.currentRoute.page.mapStateToProps()),
        });
        this.selector = makeSelectorStateful(sourceSelector, this.store);
        this.selector.run(this.currentProps);
    }

    private initSubscription() {
        const parentSub = (this.propsMode ? this.props : this.context).storeSubscription;
        this.subscription = new Subscription(this.store, parentSub, this.onStateChange);
        this.notifyNestedSubs = this.subscription.notifyNestedSubs.bind(this.subscription);
    }

    private onStateChange = () => {
        if (this.loading) {
            return;
        }

        this.selector.run(this.currentProps);

        if (!this.selector.shouldComponentUpdate) {
            this.notifyNestedSubs();
        } else {
            this.componentDidUpdate = this.notfyNestedSubsOnComponentDidUpdate;
            this.setState(dummyState);
        }
    };

    private notfyNestedSubsOnComponentDidUpdate() {
        this.componentDidUpdate = undefined;
        this.notifyNestedSubs();
    }

    private addExtraProps(props) {
        if (!(this.propsMode && this.subscription)) {
            return props;
        }

        const withExtras = { ...props };
        if (this.propsMode && this.subscription) {
            withExtras.storeSubscription = this.subscription;
        }

        return withExtras;
    }
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    return {
        baseUrl: getRequestBaseUrl(state),
        mobile: getRequestDeviceMobile(state),
        requestUserPermissions: getRequestPermissions(state),
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {};

export default connect(mapStateToProps, mapDispatchToProps)(Application);
